import React, { useCallback, useEffect, useState } from 'react';
import {Helmet} from "react-helmet";
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { Box, Button, Container, Divider, Grid, Stack, Typography } from '@mui/material';
import { APIResponse, Auction, ListingResponse, TypeSnackbarPayload } from '../utilities/types';
import { auctionList } from '../apis';
import { showSnackbar } from '../store/slices/common.slice';
import AuctionWidget from '../components/widgets/auction.widget';
import useAuctionBroadcastWsHook from '../hooks/auction-broadcast-ws.hook';
import { RootState } from '../store';

const AuctionsPage: React.FC = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const { token } = useSelector((state: RootState) => state.auth);

  const [auctions, setAuctions] = useState<Auction[]>([]);
  const [listingType, setListingType] = useState<string>('active');

  const loadAuctions = useCallback(async () => {
    try {
      const payload: any = { listing_type: listingType };
      const categoryId = searchParams.get('category_id');
      if (categoryId) {
        payload.category_id = categoryId;
      }
      const response = (await auctionList(payload)) as APIResponse<ListingResponse>;
      if (response.status) {
        setAuctions(Array.isArray(response.data?.items) ? response.data.items : []);
      }
    } catch (err: any) {
      const snakbarpayload: TypeSnackbarPayload = { severity: 'error', message: err.message };
      if (err?.data?.message) {
        snakbarpayload.message = err?.data?.message;
      } else if (err?.statusText) {
        snakbarpayload.message = err?.statusText;
      }
      dispatch(showSnackbar(snakbarpayload));
    }
  }, [dispatch, listingType, searchParams]);

  useEffect(() => {
    loadAuctions();
  }, [loadAuctions]);

  useEffect(() => {
    loadAuctions();
  }, [listingType, loadAuctions]);

  useAuctionBroadcastWsHook(token, loadAuctions);

  return (
    <>
      <Helmet>
        <title>All Auctions - Scrap E-Auction Online Scrap Metal Auction Platform</title>
        <meta name="description" content="Browse all active scrap auctions" />
      </Helmet>
      <Container sx={{ py: 4, bgcolor: '#fbf9fa' }}>
        <Box
          sx={{
            pt: 3,
            pb: 8,
            px: 2,
          }}
        >
          <Box sx={{ mb: 4 }}>
            <Typography variant="h1" gutterBottom sx={{ fontWeight: '600' }}>
              Auctions
            </Typography>
            <Typography variant="body1" gutterBottom color="textSecondary">
              Browse through all live and upcoming auctions.
            </Typography>
          </Box>
          <Stack
            flexDirection="row"
            alignItems="center"
            sx={{
              mb: 3,
              width: '100%',
            }}
            gap={3}
          >
            <Typography variant="body1">Filters By: </Typography>
            <Button
              variant={listingType === 'active' ? 'contained' : 'outlined'}
              color="info"
              onClick={() => setListingType('active')}
            >
              All
            </Button>
            <Button
              variant={listingType === 'current' ? 'contained' : 'outlined'}
              color="info"
              onClick={() => setListingType('current')}
            >
              Live
            </Button>
            <Button
              variant={listingType === 'future' ? 'contained' : 'outlined'}
              color="info"
              onClick={() => setListingType('future')}
            >
              Upcoming
            </Button>
          </Stack>
          <Divider sx={{ my: 3 }} />
          {auctions.length > 0 ? (
            <Grid container spacing={3}>
              {auctions.map((item: Auction, index) => (
                <Grid key={`auction-list-${index}`} size={{ xs: 12, sm: 2, md: 4 }}>
                  <AuctionWidget auctionDetail={item} />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box
              sx={{
                display: 'flex',
                width: '100%',
                height: 300,
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Typography variant="body2" color="text.error">
                No Auction Found
              </Typography>
            </Box>
          )}
        </Box>
      </Container>
    </>
  );
};

export default AuctionsPage;
